import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import SearchableSelect from 'components/atoms/searchable-select'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import { ENABLE_CONSIGNMENT_NOTES } from 'constants/consignment-notes-config'
import Phase3ProductSummaryCard from '../components/Phase3ProductSummaryCard'
import {
  buildPhase3ShoesSizingPayload,
  extractPhase3Product,
  formatPhase3MoneyInput,
  getPhase3ErrorMessage,
  getPhase3InsolePhotoField,
  isPhase3ShoesProduct,
  normalizePhase3ImageUrl,
  normalizePhase3InsoleSizeInput,
  normalizePhase3ShoeSizeInput,
  sanitizePhase3MoneyInput,
  validatePhase3ShoesSizing
} from '../helpers/phase3-inbound-journey-helper'

const SKIP_CONFIRM_BUYING_PRICE_GROUP_CODES = [ 'BKA', 'BKO', 'BKV' ]

const productShouldSkipConfirmBuyingPrice = (product = {}) => {
  if (typeof product.skip_confirm_buying_price === 'boolean') return product.skip_confirm_buying_price

  return !!product.skip_confirm_buying_price || SKIP_CONFIRM_BUYING_PRICE_GROUP_CODES.includes((product.group_code || '').toString().trim().toUpperCase())
}

const PRODUCT_FIELDS = [
  'name',
  'supplier_name',
  'comes_with',
  'product_description',
  'defects',
  'details',
  'condition_id',
  'stamp',
  'leather',
  'hardware',
  'p_colour',
  'p_size',
  'insole_size',
  'height',
  'length',
  'width',
  'handle_drop',
  'strap_drop',
  'size',
  'photo_folder_link',
  'buying_price',
  'default_price',
  'selling_price',
  'appraisal_note',
  'item_location_id',
  'box_location_id',
  'inclusions_location_id',
  'internal_search_keyword',
  'internal_note',
  'stockist_pic'
]

const PRODUCT_FILE_FIELDS = [
  'front_view',
  'right_side_view',
  'rear_view',
  'left_side_view',
  'top_view',
  'bottom_view',
  'comes_with_image',
  'stamp_image',
  'card_image',
  'insole_photo'
]

const PRODUCT_MULTI_FILE_FIELDS = [
  'photo_details'
]

const STUDIO_UPLOAD_MAX_FILES = 40

const EMPTY_OPTIONS = {
  conditions           : [],
  item_locations       : [],
  box_locations        : [],
  inclusions_locations : []
}

const PHOTO_FIELDS = [
  { key: 'front_view', label: 'Front View', imageKey: 'front_view_url' },
  { key: 'right_side_view', label: 'Right Side View', imageKey: 'right_side_view_url' },
  { key: 'rear_view', label: 'Rear View', imageKey: 'rear_view_url' },
  { key: 'left_side_view', label: 'Left Side View', imageKey: 'left_side_view_url' },
  { key: 'top_view', label: 'Top View', imageKey: 'top_view_url' },
  { key: 'bottom_view', label: 'Bottom View', imageKey: 'bottom_view_url' },
  { key: 'comes_with_image', label: 'Foto Kelengkapan', imageKey: 'comes_with_image_url' },
  { key: 'stamp_image', label: 'Foto Stamp', imageKey: 'stamp_image_url' },
  { key: 'card_image', label: 'Foto Card', imageKey: 'card_image_url' }
]

const SIZE_OPTIONS = [
  { value: '', label: 'Belum Diisi' },
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' }
]

const toProductForm = (product = {}) => {
  const result = {}
  PRODUCT_FIELDS.forEach((field) => {
    result[field] = product[field] == null ? '' : product[field]
  })
  PRODUCT_FILE_FIELDS.forEach((field) => {
    result[field] = null
  })
  PRODUCT_MULTI_FILE_FIELDS.forEach((field) => {
    result[field] = []
  })
  result.price_tag_done = !!product.price_tag_done
  return result
}

const optionValue = (option = {}) => option.value == null ? option.id : option.value

const optionLabel = (option = {}) => {
  if (option.code) return `${option.name} (${option.code})`

  return option.name || option.label || option.id
}

const fileSizeLabel = (file) => {
  const size = file?.size
  if (!size && size !== 0) return '-'
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`

  return `${size} B`
}

const sortBulkPhotoRows = (rows = []) => [ ...rows ].sort((a, b) => {
  const aPosition = parseInt(a.position || 0, 10)
  const bPosition = parseInt(b.position || 0, 10)

  return aPosition - bPosition
})

const photoDetailUrls = (product = {}) => (product.images?.photo_detail_urls || product.photo_detail_urls || [])
  .map((url) => normalizePhase3ImageUrl(url))
  .filter(Boolean)

const productPhotoUrl = (product = {}, field = {}) => normalizePhase3ImageUrl(
  product.images?.[field.imageKey] || product[field.imageKey] || ''
)

const collectProductUploadFiles = (nextForm = {}, nextBulkPhotoRows = []) => {
  const singleFiles = PRODUCT_FILE_FIELDS.map((field) => nextForm[field]).filter(Boolean)
  const multipleFiles = PRODUCT_MULTI_FILE_FIELDS.flatMap((field) => Array.from(nextForm[field] || []))
  const bulkFiles = (nextBulkPhotoRows || []).map((row) => row.file).filter(Boolean)

  return [ ...singleFiles, ...multipleFiles, ...bulkFiles ]
}

const Phase3ProductDetailPage = ({ pageUtils, apiNamespace: propApiNamespace = '' }) => {
  const { id } = useParams()
  const location = useLocation()
  const [ product, setProduct ] = useState(null)
  const [ form, setForm ] = useState({})
  const [ options, setOptions ] = useState(EMPTY_OPTIONS)
  const [ activeTab, setActiveTab ] = useState('product')
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ skipConfirmBuyingPrice, setSkipConfirmBuyingPrice ] = useState(false)
  const [ locationNote, setLocationNote ] = useState('')
  const [ bulkPhotoRows, setBulkPhotoRows ] = useState([])
  const bulkPhotoInputRef = useRef(null)
  const bulkPhotoRowsRef = useRef([])

  useEffect(() => {
    fetchProduct()
  }, [ id ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bulkPhotoRowsRef.current = bulkPhotoRows
  }, [ bulkPhotoRows ])

  useEffect(() => () => {
    bulkPhotoRowsRef.current.forEach((row) => {
      if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
    })
  }, [])

  const queryParams = new URLSearchParams(location.search)
  const apiNamespace = propApiNamespace || queryParams.get('api_namespace') || ''
  const isStudioNamespace = apiNamespace === 'studio'
  const isReadOnlyNamespace = [ 'all_team', 'sales' ].includes(apiNamespace)
  const canSaveProduct = !isReadOnlyNamespace && (!apiNamespace || [ 'studio', 'inbound' ].includes(apiNamespace))
  const canStudioComplete = !isReadOnlyNamespace && (!apiNamespace || apiNamespace === 'studio')
  const showSideWorkflowActions = !isReadOnlyNamespace && !isStudioNamespace
  const isShoesProduct = isPhase3ShoesProduct(product || {})
  const insolePhotoField = getPhase3InsolePhotoField(product || {})
  const productPhotoFields = insolePhotoField
    ? [ ...PHOTO_FIELDS, insolePhotoField ]
    : PHOTO_FIELDS
  const bulkPhotoKindOptions = [
    ...productPhotoFields.map((field) => ({ key: field.key, label: field.label })),
    { key: 'photo_details', label: 'Photo Details' }
  ]
  const singlePhotoFieldKeys = productPhotoFields.map((field) => field.key)
  const batchListBasePath = apiNamespace === 'all_team'
    ? '/app/all-team/inbound-journey/inventory-batches'
    : '/app/inbound-journey/inventory-batches'

  const batchListPath = (() => {
    const batchId =
      product?.consignment?.inventory_batch?.id ||
      product?.inbound?.inbound_journey_inventory_batch_id

    if (!batchId) return batchListBasePath

    const params = new URLSearchParams()
    params.set('batch_id', batchId)

    if (apiNamespace) {
      params.set('api_namespace', apiNamespace)
    }

    return `${batchListBasePath}?${params.toString()}`
  })()
  const groupCode = product?.group_code || ''
  const appraisalState = product?.appraisal_state || ''
  const isProductCuratorBkr = apiNamespace === 'product_curator' && groupCode === 'BKR'
  const canViewBuyingPrice = product?.can_view_buying_price === true
  const canViewSellingPrice = product?.can_view_selling_price !== false
  const canFillAppraisalRole = product?.can_fill_appraisal !== false && (
    !apiNamespace ||
    (apiNamespace === 'product_curator' && groupCode === 'BKR') ||
    (apiNamespace === 'finance' && groupCode === 'BKA') ||
    (apiNamespace === 'buying' && groupCode === 'BKO')
  )
  const canFillAppraisal = canFillAppraisalRole && (
    [ 'need_appraisal', 'need_re_appraisal', 'appraisal_rejected' ].includes(appraisalState) ||
    (
      [ 'BKA', 'BKO' ].includes(groupCode) &&
      [ 'need_confirm_hm', 'need_approval' ].includes(appraisalState)
    ) ||
    (
      groupCode === 'BKR' &&
      appraisalState === 'need_approval'
    )
  )
  const canManageCfBuyingPrice = product?.can_confirm_buying_price === true
  const canChooseSkipConfirmBuyingPrice = canManageCfBuyingPrice &&
    (!apiNamespace || apiNamespace === 'product_curator') &&
    groupCode === 'BKR'
  const canConfirmBuyingPrice = canManageCfBuyingPrice &&
    (!apiNamespace || [ 'product_curator', 'finance' ].includes(apiNamespace)) &&
    groupCode === 'BKR' &&
    appraisalState === 'need_confirm_hm'
  const canApproveAppraisal = product?.can_approve_appraisal !== false &&
    (!apiNamespace || apiNamespace === 'product_curator') &&
    appraisalState === 'need_approval'
  const canFinalCheck = !apiNamespace || apiNamespace === 'inbound'

  const fetchProduct = async () => {
    setIsLoading(true)
    try {
      const response = await InboundJourneyApiV2.showProduct({ id, apiNamespace, includeOptions: true })
      const nextProduct = extractPhase3Product(response)
      setProduct(nextProduct)
      setForm(toProductForm(nextProduct))
      setOptions({ ...EMPTY_OPTIONS, ...(response.data?.form_options || {}) })
      setSkipConfirmBuyingPrice(productShouldSkipConfirmBuyingPrice(nextProduct))
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || { messages: { base: message } }) : alert(message)
    } finally {
      setIsLoading(false)
    }
  }

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }))

  const setDefaultSellingPrice = (value) => {
    const sanitizedValue = sanitizePhase3MoneyInput(value)

    setForm((prev) => ({
      ...prev,
      default_price: sanitizedValue,
      ...(isProductCuratorBkr ? { selling_price: sanitizedValue } : {})
    }))
  }

  const showApiError = (messageOrPayload) => {
    const payload = typeof messageOrPayload === 'string' ? { messages: { base: messageOrPayload } } : messageOrPayload
    pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(payload) : alert(getPhase3ErrorMessage({ response: { data: payload } }))
  }

  const clearBulkPhotoRows = () => {
    bulkPhotoRows.forEach((row) => {
      if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
    })
    setBulkPhotoRows([])
  }

  const validateProductUploadFiles = (nextForm, nextBulkPhotoRows = bulkPhotoRows) => {
    const uploadFiles = collectProductUploadFiles(nextForm, nextBulkPhotoRows)

    if (uploadFiles.length > STUDIO_UPLOAD_MAX_FILES) {
      const message = `Maksimal upload ${STUDIO_UPLOAD_MAX_FILES} foto dalam 1 kali save.`
      showApiError(message)
      return false
    }

    return true
  }

  const handleBulkPhotoFiles = (files) => {
    const imageFiles = Array.from(files || []).filter((file) => /^image\//.test(file.type || ''))
    if (imageFiles.length === 0) {
      showApiError('Pilih file gambar terlebih dahulu.')
      return
    }

    const nextRows = [
      ...bulkPhotoRows,
      ...imageFiles.map((file, index) => ({
        id         : `${Date.now()}-${index}-${file.name}`,
        file       : file,
        previewUrl : URL.createObjectURL(file),
        kind       : '',
        position   : bulkPhotoRows.length + index + 1
      }))
    ]

    if (!validateProductUploadFiles(form, nextRows)) {
      nextRows.slice(bulkPhotoRows.length).forEach((row) => {
        if (row.previewUrl) URL.revokeObjectURL(row.previewUrl)
      })
      return
    }

    setBulkPhotoRows(nextRows)
  }

  const handleBulkPhotoDrop = (event) => {
    event.preventDefault()
    if (isReadOnlyNamespace) return

    handleBulkPhotoFiles(event.dataTransfer?.files)
  }

  const handleBulkPhotoDragOver = (event) => {
    event.preventDefault()
  }

  const handleBulkPhotoInputChange = (event) => {
    handleBulkPhotoFiles(event.target.files)
    event.target.value = ''
  }

  const setBulkPhotoRow = (rowId, patch) => {
    setBulkPhotoRows((prev) => prev.map((row) => {
      if (row.id === rowId) return { ...row, ...patch }
      if (patch.kind && singlePhotoFieldKeys.includes(patch.kind) && row.kind === patch.kind) return { ...row, kind: '' }

      return row
    }))
  }

  const removeBulkPhotoRow = (rowId) => {
    setBulkPhotoRows((prev) => {
      const row = prev.find((item) => item.id === rowId)
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl)

      return prev.filter((item) => item.id !== rowId)
    })
  }

  const existingPhotoKindHasImage = (kind) => {
    const field = productPhotoFields.find((photoField) => photoField.key === kind)
    return field ? !!productPhotoUrl(product || {}, field) : false
  }

  const validateBulkPhotoAssignments = () => {
    const unassignedRow = bulkPhotoRows.find((row) => !row.kind)
    if (unassignedRow) {
      showApiError(`Pilih Kind untuk semua foto bulk sebelum Save Product. Belum dipilih: ${unassignedRow.file?.name || 'file'}.`)
      return false
    }

    const blockedExistingKind = bulkPhotoRows.find((row) => isStudioNamespace && singlePhotoFieldKeys.includes(row.kind) && existingPhotoKindHasImage(row.kind))
    if (blockedExistingKind) {
      showApiError(`${blockedExistingKind.kind} sudah punya foto. Studio tidak bisa upload ulang slot foto yang sudah ada; edit/replace lewat Inventory Edit.`)
      return false
    }

    const singleKindCounts = bulkPhotoRows.reduce((acc, row) => {
      if (singlePhotoFieldKeys.includes(row.kind)) acc[row.kind] = (acc[row.kind] || 0) + 1
      return acc
    }, {})
    const duplicateSingleKind = Object.keys(singleKindCounts).find((key) => singleKindCounts[key] > 1)
    if (duplicateSingleKind) {
      showApiError(`Kind ${duplicateSingleKind} hanya boleh dipakai untuk 1 foto. Pakai Photo Details untuk banyak foto detail.`)
      return false
    }

    return true
  }

  const validateProductAction = (label) => {
    if (isStudioNamespace && label === 'Studio Complete' && !form.condition_id) {
      showApiError('Condition wajib dipilih sebelum Studio Complete.')
      return false
    }

    if (isStudioNamespace && label === 'Studio Complete' && !form.stockist_pic?.toString().trim()) {
      showApiError('PIC Stockist (Foto Detail) wajib diisi sebelum Studio Complete.')
      return false
    }

    if ([ 'Final Check', 'Studio Complete' ].includes(label)) {
      const shoesSizingError = validatePhase3ShoesSizing(product || {}, form)

      if (shoesSizingError) {
        showApiError(shoesSizingError)
        return false
      }
    }

    if (isStudioNamespace && label === 'Studio Complete' && isShoesProduct) {
      const hasExistingInsolePhoto = insolePhotoField
        ? !!productPhotoUrl(product || {}, insolePhotoField)
        : false
      const hasPendingInsolePhoto =
        !!form.insole_photo ||
        bulkPhotoRows.some((row) => row.kind === 'insole_photo' && !!row.file)

      if (!hasExistingInsolePhoto && !hasPendingInsolePhoto) {
        showApiError('Insole Photo wajib diupload untuk kategori Shoes sebelum Studio Complete.')
        return false
      }
    }

    return true
  }

  const runAction = async (label, request) => {
    if (!validateProductAction(label) || !validateProductUploadFiles(form, bulkPhotoRows) || !validateBulkPhotoAssignments()) return

    setIsSubmitting(true)
    try {
      const response = await request()
      const nextProduct = extractPhase3Product(response)
      setProduct(nextProduct)
      setForm(toProductForm(nextProduct))
      clearBulkPhotoRows()
      setSkipConfirmBuyingPrice(productShouldSkipConfirmBuyingPrice(nextProduct))
      pageUtils?.setAlertMsg?.(response.data?.message || `${label} success`)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      showApiError(error.response?.data || message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const permissions = product?.permissions || {}
  const canEditBuyingPrice = permissions.can_edit_buying_price !== false
  const canEditDefaultPrice = permissions.can_edit_default_price !== false
  const canEditSellingPrice = permissions.can_edit_selling_price !== false &&
    product?.can_edit_selling_price !== false
  const hasReadOnlyPrice = !canEditBuyingPrice || !canEditDefaultPrice || !canEditSellingPrice
  const hasPositivePrice = (value) => Number(value) > 0
  const readOnlyFillBlocked =
    (!canEditBuyingPrice && !hasPositivePrice(form.buying_price)) ||
    (!canEditDefaultPrice && !hasPositivePrice(form.default_price)) ||
    (!canEditSellingPrice && !hasPositivePrice(form.selling_price))
  const readOnlyConfirmBlocked = !canEditBuyingPrice && !hasPositivePrice(form.buying_price)

  const productPayload = () => {
    const sizingPayload = buildPhase3ShoesSizingPayload(product || {}, form)

    const payload = {
      name                             : form.name,
      supplier_name                    : form.supplier_name,
      comes_with                       : form.comes_with,
      product_description              : form.product_description,
      defects                          : form.defects,
      details                          : form.details,
      condition_id                     : form.condition_id,
      stamp                            : form.stamp,
      leather                          : form.leather,
      hardware                         : form.hardware,
      p_colour                         : form.p_colour,
      ...sizingPayload,
      height                           : form.height,
      length                           : form.length,
      width                            : form.width,
      handle_drop                      : form.handle_drop,
      strap_drop                       : form.strap_drop,
      size                             : form.size,
      photo_folder_link                : form.photo_folder_link,
      appraisal_note                   : form.appraisal_note,
      item_location_id                 : form.item_location_id,
      box_location_id                  : form.box_location_id,
      inclusions_location_id           : form.inclusions_location_id,
      internal_search_keyword          : form.internal_search_keyword,
      internal_note                    : form.internal_note,
      stockist_pic                     : form.stockist_pic,
      price_tag_done                   : form.price_tag_done,
      front_view                       : form.front_view,
      right_side_view                  : form.right_side_view,
      rear_view                        : form.rear_view,
      left_side_view                   : form.left_side_view,
      top_view                         : form.top_view,
      bottom_view                      : form.bottom_view,
      comes_with_image                 : form.comes_with_image,
      stamp_image                      : form.stamp_image,
      card_image                       : form.card_image,
      ...(isShoesProduct ? { insole_photo: form.insole_photo } : {}),
      photo_details                    : form.photo_details
    }

    if (canEditBuyingPrice) payload.buying_price = form.buying_price
    if (canEditDefaultPrice) payload.default_price = form.default_price
    if (canEditSellingPrice) payload.selling_price = form.selling_price

    sortBulkPhotoRows(bulkPhotoRows).forEach((row) => {
      if (!row.kind || !row.file) return

      if (row.kind === 'photo_details') {
        payload.photo_details = [ ...Array.from(payload.photo_details || []), row.file ]
      } else if (singlePhotoFieldKeys.includes(row.kind)) {
        payload[row.kind] = row.file
      }
    })

    if (isStudioNamespace) {
      delete payload.supplier_name
      delete payload.buying_price
      delete payload.default_price
      delete payload.selling_price
      delete payload.appraisal_note
      delete payload.price_tag_done
    }

    return payload
  }

  const renderOptions = (items, placeholder) => (
    <>
      <option value=''>{ placeholder }</option>
      { (items || []).map((option) => (
        <option key={ `${option.id}-${optionValue(option)}` } value={ optionValue(option) }>
          { optionLabel(option) }
        </option>
      )) }
    </>
  )

  const renderLocationSelect = (label, field, items, placeholder) => (
    <Col md='4'>
      <FormGroup>
        <Label>{ label }</Label>
        <SearchableSelect
          options={ (items || []).map((option) => ({ value: optionValue(option), label: optionLabel(option) })) }
          value={ form[field] || '' }
          onChange={ (value) => setField(field, value) }
          placeholder={ placeholder.replace('Select ', 'Search ') }
          isDisabled={ isReadOnlyNamespace }
        />
      </FormGroup>
    </Col>
  )

  const renderExistingPhotoTable = () => {
    const rows = [
      ...productPhotoFields.map((field) => ({
        key   : field.key,
        label : field.label,
        url   : productPhotoUrl(product || {}, field),
        note  : isStudioNamespace ? 'Already uploaded. Edit/replace via Inventory Edit if needed.' : 'Existing photo slot.'
      })),
      ...photoDetailUrls(product || {}).map((url, index) => ({
        key   : `photo-detail-${index}`,
        label : `Photo Detail ${index + 1}`,
        url   : url,
        note  : 'Photo detail.'
      }))
    ].filter((row) => row.url)

    if (rows.length === 0) {
      return <div className='p-3 bg-light rounded text-muted'>Belum ada photo preview untuk produk ini.</div>
    }

    return (
      <div className='table-responsive mb-3'>
        <table className='table table-bordered table-sm bg-white mb-0'>
          <thead>
            <tr>
              <th style={{ width: 90 }}>Preview</th>
              <th>Image Type</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            { rows.map((row) => (
              <tr key={ row.key }>
                <td>
                  <a href={ row.url } target='_blank' rel='noopener noreferrer'>
                    <img src={ row.url } alt={ row.label } style={{ width: 72, height: 72, objectFit: 'contain', background: '#f4f6f8' }} />
                  </a>
                </td>
                <td className='font-weight-bold'>{ row.label }</td>
                <td className='text-muted small'>{ row.note }</td>
              </tr>
            )) }
          </tbody>
        </table>
      </div>
    )
  }

  const renderBulkPhotoRows = () => {
    if (bulkPhotoRows.length === 0) {
      return (
        <div className='text-muted small'>
          Belum ada foto bulk dipilih. Drop/choose beberapa foto, lalu assign Kind sebelum Save Product.
        </div>
      )
    }

    return (
      <div className='table-responsive mt-3'>
        <table className='table table-bordered table-sm'>
          <thead>
            <tr>
              <th style={{ width: 80 }}>Thumb</th>
              <th>File</th>
              <th style={{ width: 220 }}>Kind</th>
              <th style={{ width: 110 }}>Position</th>
              <th style={{ width: 100 }}>Size</th>
              <th style={{ width: 90 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            { sortBulkPhotoRows(bulkPhotoRows).map((row) => (
              <tr key={ row.id }>
                <td>
                  <div className='border rounded bg-light d-flex align-items-center justify-content-center' style={{ width: 64, height: 64, overflow: 'hidden' }}>
                    <img src={ row.previewUrl } alt={ row.file?.name || 'Photo preview' } style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                </td>
                <td className='small text-break'>
                  <div className='font-weight-bold'>{ row.file?.name || '-' }</div>
                  <div className='text-muted'>Selected, not uploaded yet. It will upload when Save Product / Studio Complete is clicked.</div>
                </td>
                <td>
                  <Input disabled={ isReadOnlyNamespace } type='select' value={ row.kind || '' } onChange={ (event) => setBulkPhotoRow(row.id, { kind: event.target.value }) }>
                    <option value=''>— choose kind —</option>
                    { bulkPhotoKindOptions.map((option) => {
                      const disabled = isStudioNamespace && singlePhotoFieldKeys.includes(option.key) && existingPhotoKindHasImage(option.key)

                      return (
                        <option key={ option.key } value={ option.key } disabled={ disabled }>
                          { option.label }{ disabled ? ' — sudah ada, edit via Inventory Edit' : '' }
                        </option>
                      )
                    }) }
                  </Input>
                </td>
                <td>
                  <Input disabled={ isReadOnlyNamespace } type='number' min='0' value={ row.position } onChange={ (event) => setBulkPhotoRow(row.id, { position: event.target.value }) } />
                </td>
                <td className='small'>{ fileSizeLabel(row.file) }</td>
                <td>
                  <Button disabled={ isReadOnlyNamespace } type='button' color='danger' outline size='sm' onClick={ () => removeBulkPhotoRow(row.id) }>
                    Remove
                  </Button>
                </td>
              </tr>
            )) }
          </tbody>
        </table>
      </div>
    )
  }

  if (isLoading || !product) {
    return <div className='p-4 text-center text-muted'>Loading product...</div>
  }

  const forceSkipConfirmBuyingPrice = productShouldSkipConfirmBuyingPrice(product)
  const effectiveSkipConfirmBuyingPrice = skipConfirmBuyingPrice || forceSkipConfirmBuyingPrice

  return (
    <>
      <TitlePage mainTitle='Inbound Journey' subTitle={ isStudioNamespace ? 'Studio Detail Kelengkapan' : 'Product Detail' } />

      <div className='mb-3'>
        <Button color='info' outline tag='a' href={ batchListPath }>
          Open Batch List
        </Button>
      </div>

      <Phase3ProductSummaryCard product={ product } showBuyingPrice={ !isStudioNamespace && canViewBuyingPrice } />

      { product.consignment?.inventory_batch_code && (
        <Card className='mb-3'>
          <CardBody>
            <div className='d-flex justify-content-between align-items-center'>
              <div>
                <div className='font-weight-bold'>
                  Inventory Batch:{ ' ' }
                  <a href={ batchListPath }>{ product.consignment.inventory_batch_code }</a>
                </div>
                <div className='text-muted'>Use this batch code for physical handover and batch flow for products in the same consign.</div>
              </div>
              <Button color='info' outline tag='a' href={ batchListPath }>
                Open Batch List
              </Button>
            </div>
          </CardBody>
        </Card>
      ) }

      <Row>
        <Col lg={ isStudioNamespace ? '12' : '8' }>
          <Card>
            <CardBody>
              <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4'>
                <div className='d-flex'>
                  <Button color={ activeTab === 'product' ? 'primary' : 'secondary' } outline={ activeTab !== 'product' } className='mr-2' onClick={ () => setActiveTab('product') }>
                    Product Fields
                  </Button>
                  <Button color={ activeTab === 'photos' ? 'primary' : 'secondary' } outline={ activeTab !== 'photos' } onClick={ () => setActiveTab('photos') }>
                    Photos & Kelengkapan
                  </Button>
                </div>

                { isStudioNamespace && (canSaveProduct || canStudioComplete) && (
                  <div className='d-flex flex-wrap justify-content-md-end mt-3 mt-md-0' style={{ gap: 8 }}>
                    { canSaveProduct && (
                      <Button
                        color='primary'
                        disabled={ isSubmitting }
                        onClick={ () => runAction('Save product', () => InboundJourneyApiV2.updateProduct({ id, product: productPayload(), apiNamespace })) }
                      >
                        { isSubmitting ? 'Saving...' : 'Save Product' }
                      </Button>
                    ) }
                    { canStudioComplete && (
                      <Button color='info' disabled={ isSubmitting } onClick={ () => runAction('Studio Complete', () => InboundJourneyApiV2.studioComplete({ id, product: productPayload(), apiNamespace })) }>
                        Studio Complete
                      </Button>
                    ) }
                  </div>
                ) }
              </div>

              { activeTab === 'product' && (
                <>
                  <h4>Read-only Context</h4>
                  <Row form>
                    <Col md='3'>
                      <FormGroup>
                        <Label>SKU</Label>
                        <Input value={ product.sku || '' } disabled />
                      </FormGroup>
                    </Col>
                    <Col md='3'>
                      <FormGroup>
                        <Label>Group</Label>
                        <Input value={ [ product.group_name, product.group_code ].filter(Boolean).join(' / ') } disabled />
                      </FormGroup>
                    </Col>
                    <Col md='3'>
                      <FormGroup>
                        <Label>Internal Category</Label>
                        <Input value={ product.internal_category || '' } disabled />
                      </FormGroup>
                    </Col>
                    <Col md='3'>
                      <FormGroup>
                        <Label>Designer / Category</Label>
                        <Input value={ [ product.designer_name, product.category_name ].filter(Boolean).join(' / ') } disabled />
                      </FormGroup>
                    </Col>
                  </Row>

                  <h4 className='mt-3'>Detail Kelengkapan</h4>
                  <Row form>
                    <Col md='8'>
                      <FormGroup>
                        <Label>Name</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.name || '' } onChange={ (event) => setField('name', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Condition <span className='text-danger'>*</span></Label>
                        <Input disabled={ isReadOnlyNamespace } type='select' value={ form.condition_id || '' } onChange={ (event) => setField('condition_id', event.target.value) }>
                          { renderOptions(options.conditions, 'Select Condition') }
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  { !isStudioNamespace && (
                    <Row form>
                      { canViewBuyingPrice && (
                        <Col md={ canViewSellingPrice ? '4' : '6' }>
                          <FormGroup>
                            <Label>Buying Price</Label>
                            <Input
                              type='text'
                              inputMode='numeric'
                              disabled={ !canFillAppraisalRole || !canEditBuyingPrice }
                              value={ formatPhase3MoneyInput(form.buying_price) }
                              onChange={ (event) => setField('buying_price', sanitizePhase3MoneyInput(event.target.value)) }
                            />
                          </FormGroup>
                        </Col>
                      ) }
                      <Col md={ canViewBuyingPrice && canViewSellingPrice ? '4' : '6' }>
                        <FormGroup>
                          <Label>Default Price</Label>
                          <Input
                            type='text'
                            inputMode='numeric'
                            disabled={ !canFillAppraisalRole || !canEditDefaultPrice }
                            value={ formatPhase3MoneyInput(form.default_price) }
                            onChange={ (event) => setDefaultSellingPrice(event.target.value) }
                          />
                        </FormGroup>
                      </Col>
                      { canViewSellingPrice && (
                        <Col md='4'>
                          <FormGroup>
                            <Label>Selling Price</Label>
                            <Input
                              type='text'
                              inputMode='numeric'
                              disabled={ !canFillAppraisalRole || !canEditSellingPrice }
                              value={ formatPhase3MoneyInput(form.selling_price) }
                              onChange={ (event) => setField('selling_price', sanitizePhase3MoneyInput(event.target.value)) }
                            />
                          </FormGroup>
                        </Col>
                      ) }
                    </Row>
                  ) }

                  { !isStudioNamespace && hasReadOnlyPrice && (
                    <div className={ `alert ${readOnlyFillBlocked ? 'alert-warning' : 'alert-info'}` }>
                      HM, Default Price, dan HJ ditampilkan dalam mode read-only untuk akun ini.
                      { readOnlyFillBlocked && (
                        <div className='mt-1'>
                          Appraisal belum dapat dilanjutkan sampai harga kosong diisi oleh user yang memiliki akses edit.
                        </div>
                      ) }
                    </div>
                  ) }

                  <Row form>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Comes With</Label>
                        <Input disabled={ isReadOnlyNamespace } type='textarea' value={ form.comes_with || '' } onChange={ (event) => setField('comes_with', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Description</Label>
                        <Input disabled={ isReadOnlyNamespace } type='textarea' value={ form.product_description || '' } onChange={ (event) => setField('product_description', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Defects (Dosa)</Label>
                        <Input disabled={ isReadOnlyNamespace } type='textarea' value={ form.defects || '' } onChange={ (event) => setField('defects', event.target.value) } />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Details</Label>
                    <Input disabled={ isReadOnlyNamespace } type='textarea' value={ form.details || '' } onChange={ (event) => setField('details', event.target.value) } />
                  </FormGroup>


                  <Row form>
                    <Col md='3'>
                      <FormGroup>
                        <Label>
                          Product Size
                          { isShoesProduct && <span className='text-danger'> *</span> }
                        </Label>
                        <Input
                          disabled={ isReadOnlyNamespace }
                          value={ form.p_size || '' }
                          inputMode={ isShoesProduct ? 'decimal' : undefined }
                          placeholder={ isShoesProduct ? 'Contoh: 36.5' : undefined }
                          onChange={ (event) => setField('p_size', event.target.value) }
                          onBlur={ () => {
                            if (!isShoesProduct) return

                            const normalizedValue = normalizePhase3ShoeSizeInput(form.p_size)
                            if (normalizedValue) setField('p_size', normalizedValue)
                          } }
                        />
                        { isShoesProduct && (
                          <small className='text-muted'>
                            Angka saja tanpa prefix EU / US / UK / AUS. Desimal boleh menggunakan titik atau koma.
                          </small>
                        ) }
                      </FormGroup>
                    </Col>
                    <Col md='3'>
                      <FormGroup>
                        <Label>Size</Label>
                        <Input disabled={ isReadOnlyNamespace } type='select' value={ form.size || '' } onChange={ (event) => setField('size', event.target.value) }>
                          { SIZE_OPTIONS.map((option) => <option key={ option.value } value={ option.value }>{ option.label }</option>) }
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md='3'>
                      <FormGroup>
                        <Label>Stamp</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.stamp || '' } onChange={ (event) => setField('stamp', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='3'>
                      <FormGroup>
                        <Label>Product Colour</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.p_colour || '' } onChange={ (event) => setField('p_colour', event.target.value) } />
                      </FormGroup>
                    </Col>
                  </Row>

                  { isShoesProduct && (
                    <Row form>
                      <Col md='3'>
                        <FormGroup>
                          <Label>
                            Insole Size <span className='text-danger'>*</span>
                          </Label>
                          <Input
                            disabled={ isReadOnlyNamespace }
                            value={ form.insole_size || '' }
                            inputMode='decimal'
                            placeholder='Contoh: 24.5'
                            onChange={ (event) => setField('insole_size', event.target.value) }
                            onBlur={ () => {
                              const normalizedValue = normalizePhase3InsoleSizeInput(form.insole_size)
                              if (normalizedValue) setField('insole_size', normalizedValue)
                            } }
                          />
                          <small className='text-muted'>
                            Angka lebih dari 0. Desimal boleh menggunakan titik atau koma.
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                  ) }

                  <Row form>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Leather</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.leather || '' } onChange={ (event) => setField('leather', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Hardware</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.hardware || '' } onChange={ (event) => setField('hardware', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Google Drive Photo Folder Link</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.photo_folder_link || '' } onChange={ (event) => setField('photo_folder_link', event.target.value) } />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row form>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Height</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.height || '' } onChange={ (event) => setField('height', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Length</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.length || '' } onChange={ (event) => setField('length', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Width</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.width || '' } onChange={ (event) => setField('width', event.target.value) } />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row form>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Handle Drop</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.handle_drop || '' } onChange={ (event) => setField('handle_drop', event.target.value) } />
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <Label>Strap Drop</Label>
                        <Input disabled={ isReadOnlyNamespace } value={ form.strap_drop || '' } onChange={ (event) => setField('strap_drop', event.target.value) } />
                      </FormGroup>
                    </Col>
                  </Row>

                  { isStudioNamespace && (
                    <FormGroup>
                      <Label>PIC Stockist (Foto Detail) <span className='text-danger'>*</span></Label>
                      <Input disabled={ isReadOnlyNamespace } value={ form.stockist_pic || '' } onChange={ (event) => setField('stockist_pic', event.target.value) } />
                    </FormGroup>
                  ) }

                  <Row form>
                    { renderLocationSelect('Lokasi Produk', 'item_location_id', options.item_locations, 'Select Lokasi Produk') }
                    { renderLocationSelect('Data Rak (Lokasi Box)', 'box_location_id', options.box_locations, 'Select Lokasi Box') }
                    { renderLocationSelect('Lokasi Kelengkapan', 'inclusions_location_id', options.inclusions_locations, 'Select Lokasi Kelengkapan') }
                  </Row>

                  <FormGroup>
                    <Label>Internal Search Keyword</Label>
                    <Input disabled={ isReadOnlyNamespace } value={ form.internal_search_keyword || '' } onChange={ (event) => setField('internal_search_keyword', event.target.value) } />
                  </FormGroup>

                  <FormGroup>
                    <Label>Catatan Internal</Label>
                    <Input disabled={ isReadOnlyNamespace } type='textarea' value={ form.internal_note || '' } onChange={ (event) => setField('internal_note', event.target.value) } />
                  </FormGroup>

                  { ENABLE_CONSIGNMENT_NOTES && (
                    <FormGroup>
                      <Label>Consignment Notes</Label>
                      <Input type='textarea' rows='3' value={ product.consignment_notes || '' } placeholder='No consignment notes recorded.' readOnly />
                      { Object.prototype.hasOwnProperty.call(product, 'consignment_notes')
                        ? <small className='text-muted'>Notes from Create Consignment (read-only).</small>
                        : <small className='text-warning'>Consignment Notes is not included in the API response. Check backend deployment.</small> }
                    </FormGroup>
                  ) }

                  { !isStudioNamespace && (
                    <>
                      <FormGroup>
                        <Label>Appraisal Note</Label>
                        <Input
                          type='textarea'
                          rows='4'
                          value={ form.appraisal_note || '' }
                          placeholder='Isi catatan appraisal'
                          onChange={ (event) => setField('appraisal_note', event.target.value) }
                        />
                      </FormGroup>

                      <FormGroup check className='mb-3'>
                        <Label check>
                          <Input type='checkbox' checked={ !!form.price_tag_done } onChange={ (event) => setField('price_tag_done', event.target.checked) } />{' '}
                          price_tag_done
                        </Label>
                      </FormGroup>
                    </>
                  ) }
                </>
              ) }

              { activeTab === 'photos' && (
                <>
                  <h4>Existing Photos</h4>
                  <p className='text-muted mb-3'>Preview foto existing tetap tampil dalam table. Untuk Studio, slot foto yang sudah ada tidak di-upload ulang dari halaman ini.</p>
                  { renderExistingPhotoTable() }

                  <h4 className='mt-3'>Bulk Upload Photos</h4>
                  <p className='text-muted mb-3'>Drop / choose beberapa foto sekaligus, assign Kind di table, lalu klik Save Product. Maksimal { STUDIO_UPLOAD_MAX_FILES } foto dalam 1 kali save. Validasi ukuran file dimatikan sementara sampai auto-compress aktif.</p>
                  <div
                    className='border rounded text-center p-4 mb-3'
                    style={{ backgroundColor: '#fafafa', borderStyle: 'dashed' }}
                    onDrop={ handleBulkPhotoDrop }
                    onDragOver={ handleBulkPhotoDragOver }
                  >
                    <div className='font-weight-bold mb-2'>Drop files here</div>
                    <Button disabled={ isReadOnlyNamespace } type='button' color='primary' size='sm' onClick={ () => bulkPhotoInputRef.current?.click() }>
                      Choose Files
                    </Button>
                    <input
                      ref={ bulkPhotoInputRef }
                      type='file'
                      multiple
                      accept='image/*'
                      style={{ display: 'none' }}
                      onChange={ handleBulkPhotoInputChange }
                    />
                    { bulkPhotoRows.length > 0 && (
                      <div className='mt-3 text-muted'>
                        { bulkPhotoRows.length } file(s) selected
                        <Button disabled={ isReadOnlyNamespace } type='button' color='link' size='sm' className='ml-2 p-0' onClick={ clearBulkPhotoRows }>
                          Clear
                        </Button>
                      </div>
                    ) }
                  </div>

                  <Card className='mb-4'>
                    <CardBody>
                      <div className='d-flex justify-content-between align-items-center mb-2'>
                        <h5 className='mb-0'>Assign Photo Kind</h5>
                        <small className='text-muted'>Front/Rear/etc only 1 photo each. Use Photo Details for many detail photos.</small>
                      </div>
                      { renderBulkPhotoRows() }
                    </CardBody>
                  </Card>

                </>
              ) }

              { !isStudioNamespace && (canSaveProduct || canStudioComplete) && (
                <div className='d-flex align-items-center flex-wrap' style={{ gap: 8 }}>
                  { canSaveProduct && (
                    <Button
                      color='primary'
                      disabled={ isSubmitting }
                      onClick={ () => runAction('Save product', () => InboundJourneyApiV2.updateProduct({ id, product: productPayload(), apiNamespace })) }
                    >
                      { isSubmitting ? 'Saving...' : 'Save Product' }
                    </Button>
                  ) }
                  { isStudioNamespace && canStudioComplete && (
                    <Button color='info' disabled={ isSubmitting } onClick={ () => runAction('Studio Complete', () => InboundJourneyApiV2.studioComplete({ id, product: productPayload(), apiNamespace })) }>
                      Studio Complete
                    </Button>
                  ) }
                </div>
              ) }
            </CardBody>
          </Card>
        </Col>
        { showSideWorkflowActions && (
          <Col lg='4'>
            <Card className='mb-3'>
            <CardBody>
              <h4>Workflow Actions</h4>
              <div className='d-flex flex-column' style={{ gap: 8 }}>
                { canStudioComplete && (
                  <Button color='info' disabled={ isSubmitting } onClick={ () => runAction('Studio Complete', () => InboundJourneyApiV2.studioComplete({ id, product: productPayload(), apiNamespace })) }>
                    Studio Complete
                  </Button>
                ) }

                <FormGroup check>
                  <Label check>
                    <Input type='checkbox' checked={ effectiveSkipConfirmBuyingPrice } disabled={ forceSkipConfirmBuyingPrice } onChange={ (e) => setSkipConfirmBuyingPrice(e.target.checked) } />{' '}
                    Skip CF Buying Price
                  </Label>
                  { forceSkipConfirmBuyingPrice && <div className='small text-muted'>Auto skipped for BKA, BKO, and BKV.</div> }
                </FormGroup>
                <Button color='warning' disabled={ isSubmitting || readOnlyFillBlocked } onClick={ () => runAction('Fill Appraisal', () => InboundJourneyApiV2.fillAppraisal({ id, product: productPayload(), skip_confirm_buying_price: effectiveSkipConfirmBuyingPrice })) }>
                  Fill Appraisal
                </Button>
                <Button color='warning' disabled={ isSubmitting || readOnlyConfirmBlocked } onClick={ () => runAction('Confirm Buying Price', () => InboundJourneyApiV2.confirmBuyingPrice({ id, product: productPayload() })) }>
                  Confirm Buying Price
                </Button>
                <Button color='success' disabled={ isSubmitting } onClick={ () => runAction('Approve Appraisal', () => InboundJourneyApiV2.approveAppraisal({ id })) }>
                  Approve Appraisal
                </Button>
                <FormGroup>
                  <Label>Reject Appraisal Note</Label>
                  <Input type='textarea' value={ form.appraisal_note || '' } onChange={ (e) => setField('appraisal_note', e.target.value) } />
                </FormGroup>
                <Button color='danger' disabled={ isSubmitting } onClick={ () => runAction('Reject Appraisal', () => InboundJourneyApiV2.rejectAppraisal({ id, appraisal_note: form.appraisal_note })) }>
                  Reject Appraisal
                </Button>
                <Button color='info' disabled={ isSubmitting } onClick={ () => runAction('Final Check', () => InboundJourneyApiV2.finalCheck({ id, product: productPayload() })) }>
                  Final Check
                </Button>
                <FormGroup>
                  <Label>Live Location / Note</Label>
                  <Input value={ locationNote } onChange={ (e) => setLocationNote(e.target.value) } />
                </FormGroup>
                <Button color='success' disabled={ isSubmitting } onClick={ () => runAction('Go Live', () => InboundJourneyApiV2.goLive({ id, location: locationNote })) }>
                  Go Live
                </Button>
              </div>
            </CardBody>
            </Card>
          </Col>
        ) }
      </Row>
    </>
  )
}

export default Phase3ProductDetailPage
